import { useEffect, useState, useRef } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
  FormTextInputV2,
  FormNumberInputV2,
  FormUploadImageInputV2,
  FormDatePickerV2Input,
  FormAutocompleteV2,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  ProductGroupsActions,
  ProductsActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';
import { Autocomplete, RadioGroup } from '@wajeez-web/components/mui';
import { WajeezList } from '@wajeez-web/components/list-builder';
import _ from 'lodash';
import { FiTrash } from 'react-icons/fi';
import { enumMapper } from '@wajeez-web/core/utils';

const purchaseSources = enumMapper(Server.PurchaseSource);

type Input = Server.SetProductsToProductGroupInput;

interface SetProductsToProductGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id: number;
}

export const SetProductsToProductGroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: SetProductsToProductGroupModalProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const productDispatch = useProductDispatch();

  const [currentSelectedProduct, setCurrentSelectedProduct] =
    useState<Partial<Server.ProductOutput>>();
  const [formValues, setFormValues] = useState<Input>();

  const setProductsToProductGroupStatus = useProductSelector(
    ({ productGroups }) => productGroups.setProductsToProductGroup.status
  );
  const productGroupByIdWithProducts = useProductSelector(
    ({ productGroups }) => productGroups.productGroupByIdWithProducts
  );

  const productsLookups = useProductSelector(
    ({ products }) => products.products
  );

  const {
    handleSubmit,
    control,
    watch,
    clearErrors,
    formState: { isSubmitted, isSubmitSuccessful },
    trigger,
    reset,
  } = useForm<Input>({
    defaultValues: {},
  });

  watch((data) => {
    const dataTyped = data as Input;

    setFormValues(dataTyped);
  });

  const { replace: replaceProducts, prepend: prependProduct } = useFieldArray({
    control,
    name: 'products',
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const mappedData: Input = {
      ...data,
      products: data.products?.map((p) => {
        return {
          ...p,
          badge: p.badge ? p.badge : undefined,
        };
      }),
    };

    const res = await productDispatch(
      ProductGroupsActions.setProductsToProductGroupAsync({
        ...mappedData,
        productGroupId: id,
        languageId: 1,
      })
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (productGroupByIdWithProducts.data) {
      reset({
        products: productGroupByIdWithProducts.data.products?.map((d) => {
          return {
            productId: d.id,
            badge: d.badge,
          };
        }),
      });
    }
  }, [productGroupByIdWithProducts.data]);

  useEffect(() => {
    if (id) {
      productDispatch(
        ProductGroupsActions.getProductGroupByIdWithProductsAsync(id)
      );
    }

    return () => {
      productDispatch(ProductGroupsActions.clearProductGroupWithProducts());
    };
  }, [id, productDispatch]);

  useEffect(() => {
    productDispatch(ProductsActions.getProductsAsync());
  }, [productDispatch]);

  const renderProducts = () => {
    return (
      <WajeezList
        data={formValues?.products || []}
        keyExtractor={(p) => p.productId?.toString() || ''}
        containerType="rows"
        onOrder={(productsOrdered) => {
          if (
            !_.isEqual(
              formValues?.products?.map((p) => p.productId),
              productsOrdered.map((p) => p.productId)
            )
          ) {
            replaceProducts(productsOrdered);

            if (isSubmitted && !isSubmitSuccessful) {
              clearErrors();

              trigger();
            }
          }
        }}
        itemsRerender={true}
        handleNoData={false}
        renderItem={(p, i) => {
          const product = productsLookups.data.find(
            (pp) => pp.id === p.productId
          );

          return (
            <div className="w-full p-2 pb-4 flex justify-start items-center space-s-3">
              <div className="w-full">
                <div className="font-medium">
                  {product?.name +
                    ' - ' +
                    product?.purchaseSource +
                    ' - ' +
                    product?.purchaseProviderKey}
                </div>
                <div className="mt-3 w-full">
                  <div className="w-[300px]">
                    <FormTextInputV2
                      name={`products.${i}.badge`}
                      control={control}
                      fieldMeta={{
                        fieldType: Server.FieldType.Text,
                        fieldOptions: {},
                        label: 'Badge',
                        placeHolder: 'Badge',
                      }}
                    />
                  </div>
                </div>
              </div>
              <div
                className="text-[19px] text-red-500 cursor-pointer hover:opacity-80 active:opacity-100"
                onClick={() => {
                  const product = formValues?.products?.find(
                    (pp) => pp.productId === p.productId
                  );

                  if (product && formValues?.products) {
                    replaceProducts(
                      formValues.products.filter(
                        (pp) => pp.productId !== product.productId
                      )
                    );

                    if (isSubmitted && !isSubmitSuccessful) {
                      clearErrors();

                      trigger();
                    }
                  }
                }}
              >
                <FiTrash />
              </div>
            </div>
          );
        }}
      />
    );
  };

  const isLoading =
    productGroupByIdWithProducts.status === 'loading' ||
    productsLookups.status === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Set Products to Product Group"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
      withScroll
    >
      <div className="w-full">
        {isLoading ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <div>
            <div className="w-full flex justify-start items-center space-s-4">
              <div className="w-[300px]">
                <Autocomplete
                  options={productsLookups.data
                    .filter(
                      (p) =>
                        !formValues?.products
                          ?.map((v) => v.productId)
                          ?.includes(p.id)
                    )
                    .map((p) => {
                      return {
                        value: p.id?.toString() || '',
                        label:
                          purchaseSources.find(
                            (ps) => ps.value === p.purchaseSource?.toString()
                          )?.label +
                            ' - ' +
                            p.name +
                            ' - ' +
                            p.purchaseProviderKey || '',
                      };
                    })}
                  onChange={(option) => {
                    const product = productsLookups.data.find(
                      (p) => p.id == option
                    );

                    setCurrentSelectedProduct(product);
                  }}
                  value={currentSelectedProduct?.id?.toString()}
                />
              </div>
              <div className="w-[90px]">
                <ButtonHeadless
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={() => {
                    if (currentSelectedProduct) {
                      prependProduct({
                        productId: currentSelectedProduct?.id,
                        badge: '',
                      });

                      setCurrentSelectedProduct(undefined);
                    }
                  }}
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text={`buttons.${'Add Product'}`}
                      origintext="Add Product"
                    />
                  </div>
                </ButtonHeadless>
              </div>
            </div>
            <form
              ref={formRef}
              onSubmit={handleSubmit(onFormSubmit)}
              noValidate
              className="mt-6 w-full flex flex-col justify-start items-start"
            >
              <div className="w-full min-h-[300px] flex flex-col justify-start items-start space-y-2">
                {renderProducts()}
              </div>
              <div className="mt-6 w-full flex justify-end items-center space-s-2">
                <div className="w-[70px]">
                  <ButtonHeadless
                    bgStyle="bg-warmGray-300"
                    borderStyle="border-none"
                    paddingYStyle="py-1"
                    action={onClose}
                  >
                    <div className="text-warmGray-600 text-[16px] font-medium">
                      <ReturnTranslatedText
                        text="buttons.Cancel"
                        origintext="Cancel"
                      />
                    </div>
                  </ButtonHeadless>
                </div>
                <div className="w-[70px]">
                  <ButtonHeadless
                    type="submit"
                    borderStyle="border-none"
                    paddingYStyle="py-1"
                  >
                    <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                      <ReturnTranslatedText
                        text="buttons.Save"
                        origintext="Save"
                      />
                      {setProductsToProductGroupStatus === 'loading' && (
                        <BiLoaderAlt className="animate-spin w-5" />
                      )}
                    </div>
                  </ButtonHeadless>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
};
